import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import {
  ZInCircle,
  ZMonolith,
  ZSlice,
  Wordmark,
} from "@/components/logos/Marks";

const variants = [
  {
    id: "monolith",
    code: "Z / 01",
    name: "MONOLITH",
    tagline: "The bedrock.",
    description:
      "Uniform-weight Z — top bar, diagonal, and bottom bar share the same visual thickness. Sharp mitered corners, no ornament. Reads as serious enterprise infrastructure.",
    Mark: ZMonolith,
    notes: [
      "All three strokes are the exact same weight",
      "Best legibility at 16 px favicon",
      "Ages well · no visual noise",
    ],
  },
  {
    id: "slice",
    code: "Z / 02",
    name: "SLICE",
    tagline: "AI processing, embodied.",
    description:
      "Same uniform stroke weight as Monolith, split by a thin horizontal gap in the diagonal — implies data being sliced, streamed, processed. Most distinctive.",
    Mark: ZSlice,
    notes: [
      "Same weight as Monolith",
      "Reads most AI-native",
      "Slice flattens gracefully at 16 px",
    ],
  },
];

const SizeStrip = ({ variant }) => (
  <div className="flex items-end gap-8">
    {[16, 24, 32, 48, 64, 96].map((px) => (
      <div key={px} className="flex flex-col items-center gap-2">
        <ZInCircle
          variant={variant}
          size=""
          style={{ width: `${px}px`, height: `${px}px` }}
          innerRatio="snug"
        />
        <span className="font-mono-label text-[9px] text-zinc-500">{px}</span>
      </div>
    ))}
  </div>
);

const ContextRow = ({ variant }) => (
  <div className="grid grid-cols-3 gap-3">
    {/* Default black on white */}
    <div className="aspect-square rounded-xl bg-white border border-zinc-200 grid place-items-center">
      <ZInCircle variant={variant} size="h-16 w-16" />
    </div>
    {/* Muted background */}
    <div className="aspect-square rounded-xl bg-[#F4F4F5] border border-zinc-200 grid place-items-center">
      <ZInCircle variant={variant} size="h-16 w-16" />
    </div>
    {/* On black */}
    <div className="aspect-square rounded-xl bg-zinc-950 grid place-items-center">
      <ZInCircle
        variant={variant}
        size="h-16 w-16"
        bg="bg-white"
        fg="text-zinc-950"
      />
    </div>
  </div>
);

const InsetRow = ({ variant }) => (
  <div className="flex items-center gap-8">
    {[
      { label: "loose", inset: "loose" },
      { label: "snug", inset: "snug" },
      { label: "tight", inset: "tight" },
    ].map(({ label, inset }) => (
      <div key={label} className="flex flex-col items-center gap-2">
        <ZInCircle variant={variant} size="h-20 w-20" innerRatio={inset} />
        <span className="font-mono-label text-[9px] text-zinc-500">
          {label}
        </span>
      </div>
    ))}
  </div>
);

const LockupRow = ({ variant }) => (
  <div className="space-y-3">
    <div className="rounded-xl border border-zinc-200 bg-white p-5 flex items-center gap-3">
      <ZInCircle variant={variant} size="h-9 w-9" />
      <Wordmark size="text-xl" accentClass="text-[#0047FF]" />
    </div>
    <div className="rounded-xl border border-zinc-800 bg-zinc-950 p-5 flex items-center gap-3">
      <ZInCircle
        variant={variant}
        size="h-9 w-9"
        bg="bg-white"
        fg="text-zinc-950"
      />
      <Wordmark
        size="text-xl"
        className="text-white"
        accentClass="text-[#7AA2FF]"
      />
    </div>
    <div className="rounded-xl border border-zinc-200 bg-[#F8F9FA] p-5 flex items-center gap-3">
      <ZInCircle variant={variant} size="h-9 w-9" />
      <div>
        <Wordmark size="text-lg" accentClass="text-[#0047FF]" />
        <div className="font-mono-label text-[9px] text-zinc-500 mt-0.5">
          AI-native RCM
        </div>
      </div>
    </div>
  </div>
);

const FaviconRow = ({ variant }) => (
  <div className="flex items-center gap-6">
    {[
      { px: 48, label: "48" },
      { px: 32, label: "32" },
      { px: 24, label: "24" },
      { px: 16, label: "16" },
    ].map(({ px, label }) => (
      <div key={px} className="flex flex-col items-center gap-2">
        <ZInCircle
          variant={variant}
          size=""
          style={{ width: `${px}px`, height: `${px}px` }}
          innerRatio="snug"
        />
        <span className="font-mono-label text-[9px] text-zinc-500">
          {label}px
        </span>
      </div>
    ))}
  </div>
);

const HeroPreview = ({ variant, active, onSelect }) => {
  const v = variants.find((x) => x.id === variant);
  return (
    <button
      type="button"
      data-testid={`z-hero-${variant}`}
      onClick={() => onSelect(variant)}
      className={`group relative aspect-[4/5] rounded-2xl border overflow-hidden text-left transition-all duration-300 ${
        active
          ? "border-zinc-950 shadow-[0_30px_80px_-30px_rgba(9,9,11,0.35)] -translate-y-1"
          : "border-zinc-200 hover:-translate-y-0.5 hover:border-zinc-300"
      }`}
    >
      <div className="absolute inset-0 bg-white" />
      <div className="absolute inset-0 flex items-center justify-center">
        <ZInCircle variant={variant} size="h-40 w-40 sm:h-52 sm:w-52" />
      </div>
      {active && (
        <div className="absolute top-4 right-4 h-7 w-7 rounded-full bg-zinc-950 text-white grid place-items-center">
          <Check size={14} />
        </div>
      )}
      <div className="absolute bottom-0 inset-x-0 p-5 border-t border-zinc-200 bg-white/85 backdrop-blur">
        <div className="flex items-center justify-between">
          <span className="font-mono-label text-[10px] text-[#0047FF]">
            {v.code}
          </span>
          <span className="font-mono-label text-[10px] text-zinc-500">
            {v.name}
          </span>
        </div>
        <div className="font-display mt-2 text-xl font-semibold tracking-tight text-zinc-950">
          {v.tagline}
        </div>
      </div>
    </button>
  );
};

const LogoLab = () => {
  const [active, setActive] = useState("monolith");
  const current = variants.find((v) => v.id === active);

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-zinc-950">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/75 border-b border-zinc-200">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-sm text-zinc-700 hover:text-zinc-950"
            data-testid="logolab-back"
          >
            <ArrowLeft size={16} />
            Back to site
          </Link>
          <span className="font-mono-label text-[10px] text-zinc-500">
            Logo Lab · Z-in-circle deep dive
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 lg:px-10 py-16 space-y-20">
        {/* Intro */}
        <section>
          <span className="font-mono-label text-[10px] text-[#0047FF]">
            // Direction · white Z on black circle
          </span>
          <h1 className="font-display mt-3 text-5xl sm:text-6xl font-semibold tracking-tighter text-zinc-950 leading-[1]">
            Two Zs.
            <br />
            One black circle.
          </h1>
          <p className="mt-6 text-lg text-zinc-600 max-w-2xl">
            Uniform stroke weight across every part of the letter. Click a
            card to deep-dive: contexts, sizes, insets, favicons, and full
            lockups.
          </p>
        </section>

        {/* 3-up hero picker */}
        <section>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            {variants.map((v) => (
              <HeroPreview
                key={v.id}
                variant={v.id}
                active={active === v.id}
                onSelect={setActive}
              />
            ))}
          </div>
        </section>

        {/* Deep-dive of the currently selected */}
        <section className="rounded-3xl bg-white border border-zinc-200 p-8 sm:p-12 space-y-14">
          <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-10 items-center">
            <div className="w-full lg:w-[280px] aspect-square rounded-2xl bg-white border border-zinc-200 grid place-items-center">
              <ZInCircle variant={active} size="h-48 w-48" />
            </div>
            <div>
              <span className="font-mono-label text-[10px] text-[#0047FF]">
                {current.code} · {current.name}
              </span>
              <h2 className="font-display mt-3 text-4xl sm:text-5xl font-semibold tracking-tighter text-zinc-950">
                {current.tagline}
              </h2>
              <p className="mt-4 text-lg text-zinc-600 max-w-xl">
                {current.description}
              </p>
              <ul className="mt-6 space-y-2 text-sm text-zinc-700">
                {current.notes.map((n) => (
                  <li key={n} className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#0047FF]" />
                    {n}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Sizes */}
          <div>
            <div className="flex items-baseline justify-between mb-6">
              <div>
                <div className="font-mono-label text-[10px] text-zinc-500">
                  // Size hierarchy
                </div>
                <div className="text-sm text-zinc-700 mt-1">
                  Same mark, 16 → 96 px. Bar weight preserved.
                </div>
              </div>
            </div>
            <SizeStrip variant={active} />
          </div>

          {/* Inset ratios */}
          <div>
            <div className="font-mono-label text-[10px] text-zinc-500 mb-6">
              // Z-to-circle inset · loose · snug · tight
            </div>
            <InsetRow variant={active} />
          </div>

          {/* Contexts */}
          <div>
            <div className="font-mono-label text-[10px] text-zinc-500 mb-6">
              // Contexts · white · neutral · dark (inverts to black Z on
              white circle)
            </div>
            <ContextRow variant={active} />
          </div>

          {/* Lockups */}
          <div>
            <div className="font-mono-label text-[10px] text-zinc-500 mb-6">
              // Wordmark lockups
            </div>
            <LockupRow variant={active} />
          </div>

          {/* Favicons */}
          <div>
            <div className="font-mono-label text-[10px] text-zinc-500 mb-6">
              // Browser favicon · 48 → 16 px
            </div>
            <div className="rounded-2xl bg-[#F8F9FA] border border-zinc-200 p-8">
              <FaviconRow variant={active} />
            </div>
          </div>
        </section>

        {/* Side-by-side comparison at fav sizes */}
        <section>
          <div className="font-mono-label text-[10px] text-[#0047FF] mb-2">
            // Head-to-head at favicon sizes
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950">
            Which one still reads at 16 px?
          </h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl">
            {variants.map((v) => (
              <div
                key={v.id}
                className="rounded-2xl border border-zinc-200 bg-white p-6"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono-label text-[10px] text-zinc-500">
                    {v.code}
                  </span>
                  <span className="font-mono-label text-[10px] text-zinc-950">
                    {v.name}
                  </span>
                </div>
                <div className="mt-6 flex items-end gap-6">
                  {[64, 40, 24, 16].map((px) => (
                    <div
                      key={px}
                      className="flex flex-col items-center gap-2"
                    >
                      <ZInCircle
                        variant={v.id}
                        style={{ width: `${px}px`, height: `${px}px` }}
                        size=""
                        innerRatio="snug"
                      />
                      <span className="font-mono-label text-[9px] text-zinc-500">
                        {px}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* To pick */}
        <section className="rounded-2xl border border-zinc-200 bg-white p-8">
          <span className="font-mono-label text-[10px] text-zinc-500">
            // To pick
          </span>
          <p className="mt-3 text-zinc-700">
            Reply with{" "}
            <span className="font-mono-label text-[#0047FF]">Z / 01</span> or{" "}
            <span className="font-mono-label text-[#0047FF]">Z / 02</span>.
            Tell me any tweak (bolder bars, tighter inset, off-black circle,
            rounded joins) and I&apos;ll ship it into the live nav, footer,
            favicon and OG image.
          </p>
        </section>
      </main>
    </div>
  );
};

export default LogoLab;
